import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dropdown,
  DropdownList,
  DropdownItem,
  MenuToggle,
  MenuToggleElement,
  Tooltip,
} from '@patternfly/react-core';

interface VersionsDropdownProps {
  className?: string;
  code: string;
  currentVersion: string;
  displayTooltip?: boolean;
  isLatestVersion?: boolean;
  onOptionSelect: (version: string | number | undefined) => void;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  versions: string[];
}

const VersionsDropdown: React.FC<VersionsDropdownProps> = ({
  className,
  code,
  currentVersion,
  displayTooltip = true,
  isLatestVersion = true,
  onOptionSelect,
  tooltipPosition = 'top',
  versions,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    if (displayTooltip) {
      setShowTooltip(!isLatestVersion);
    }
  }, [displayTooltip, isLatestVersion]);

  const onToggleClick = () => {
    setIsOpen((prevValue) => !prevValue);
    setShowTooltip(false);
  };

  const onFocus = () => {
    const element = document.getElementById('course-version-toggle');
    element?.focus();
  };

  const onSelect = () => {
    setIsOpen(false);
    onFocus();
  };

  const handleOptionSelect = (version: string) => {
    setIsOpen(false);
    onFocus();
    onOptionSelect(version);
  };

  return (
    <Tooltip
      content={t('A later version of this course is available')}
      isVisible={showTooltip}
      onClick={() => setShowTooltip(false)}
      trigger="manual"
      zIndex={10}
      position={tooltipPosition}
    >
      <Dropdown
        id="course-version"
        isOpen={isOpen}
        onSelect={onSelect}
        onOpenChange={(open) => setIsOpen(open)}
        toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
          <MenuToggle
            ref={toggleRef}
            className={className}
            id="course-version-toggle"
            variant="secondary"
            onClick={onToggleClick}
            isExpanded={isOpen}
            style={{ width: '100%' }}
            aria-label={t('Click to  open course version selector')}
          >
            {t('Version {{version}}', { version: currentVersion })}
          </MenuToggle>
        )}
      >
        <DropdownList>
          {versions.map((version) => (
            <DropdownItem
              key={version}
              onClick={() => handleOptionSelect(version)}
              data-analytics-id={`version-option-${code}-${version}`}
              aria-label={t('Visit version {{version}}', { version })}
            >
              {version}
            </DropdownItem>
          ))}
        </DropdownList>
      </Dropdown>
    </Tooltip>
  );
};

export default VersionsDropdown;
